import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { serviceCityHeadScripts } from "@/lib/cityJsonLd";
import { ServicePage } from "@/components/ServicePage";
import {
  DROP_SCREEN_CITIES,
  getDropScreenCity,
  type DropScreenCity,
} from "@/data/motorizedDropScreensCities";
import hero from "@/assets/solair-pro-exterior-vertical-shade-patio.jpg";

export const Route = createFileRoute("/motorized-drop-screens/in/$city")({
  beforeLoad: ({ params }) => {
    if (!getDropScreenCity(params.city)) throw notFound();
  },
  loader: ({ params }) => ({ city: getDropScreenCity(params.city)! }),
  head: (ctx) => {
    const city = getDropScreenCity(ctx.params.city);
    if (!city) return {};
    const title = `Motorized Drop Screens in ${city.name}, TX | Solair® Pro Dealer`;
    const description = `Motorized drop screens & exterior vertical shades in ${city.name}, TX. Authorized Solair® Pro dealer — Somfy® motors, Sunbrella® & Phifertex® mesh, welded side tracks. Free on-site quotes.`;
    return {
      links: [canonicalLink(`/motorized-drop-screens/in/${city.slug}`)],
      meta: routeMeta(ctx, {
        title,
        description,
        image: hero,
        imageAlt: `Motorized drop screen lowered over a patio in ${city.name}, TX`,
        socialTitle: `Motorized Drop Screens in ${city.name}, TX — Solair® Pro Dealer`,
        socialDescription: description,
      }),
      scripts: [
        ...serviceCityHeadScripts({
          path: `/motorized-drop-screens/in/${city.slug}`,
          serviceName: "Motorized Drop Screens",
          city: city.name,
          region: city.region,
          parentPath: "/exterior-vertical-shades",
          parentName: "Motorized Screens",
        }),
        ...serviceHeadScripts({
          path: `/motorized-drop-screens/in/${city.slug}`,
          serviceName: `Motorized Drop Screens in ${city.name}, TX`,
          breadcrumbName: `Motorized Drop Screens in ${city.name}`,
          description,
        }),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Motorized Drop Screens in ${city.name}, TX`,
            serviceType: "Exterior Vertical Shade / Motorized Drop Screen Installation",
            url: canonicalUrl(`/motorized-drop-screens/in/${city.slug}`),
            provider: { "@id": "https://texasmadeshade.com/#business" },
            areaServed: {
              "@type": "City",
              name: city.name,
              containedInPlace: { "@type": "AdministrativeArea", name: city.region },
            },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-4xl font-bold text-navy-deep">City not found</h1>
      <p className="mt-4 text-navy-deep/75">
        We don't have a dedicated motorized drop screens page for that city yet.{" "}
        <Link to="/exterior-vertical-shades" className="text-red underline">
          See our main exterior vertical shades page
        </Link>
        .
      </p>
    </main>
  ),
  component: MotorizedDropScreensCityPage,
});

function MotorizedDropScreensCityPage() {
  const { city } = Route.useLoaderData() as { city: DropScreenCity };
  const nearby = city.nearby
    .map((s) => getDropScreenCity(s))
    .filter((c): c is DropScreenCity => Boolean(c));

  return (
    <ServicePage
      currentPath={`/motorized-drop-screens/in/${city.slug}`}
      serviceName={`Motorized Drop Screens in ${city.name}`}
      breadcrumbs={[
        { label: "Exterior Vertical Shades", to: "/exterior-vertical-shades" },
        { label: `Motorized Drop Screens in ${city.name}, TX` },
      ]}
      eyebrow={`★ MOTORIZED DROP SCREENS · ${city.name.toUpperCase()}, TX ★`}
      title={`Motorized Drop Screens in ${city.name}, TX`}
      intro={`Authorized Solair® Pro dealer installing motorized exterior drop screens across ${city.name} and ${city.region}. ${city.blurb} Somfy® tubular motors, welded side-track hardware, Sunbrella® and Phifertex® Plus mesh — designed, fabricated and installed by our own crew out of our Seguin, TX shop.`}
      heroImage={hero}
      heroCta={{
        eyebrow: `Solair® Pro Dealer · Serving ${city.name}, TX`,
        headline: `${city.name} Motorized Drop Screen Quote`,
        subhead: `Tell us about your patio, storefront, or outdoor kitchen in ${city.name} and we'll follow up with a written, itemized quote.`,
        primaryHref: `/exterior-vertical-shades#vertical-quote`,
        primaryLabel: `Get My ${city.name} Drop Screen Quote`,
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        `Motorized drop screen installation in ${city.name}, TX`,
        "Authorized Solair® Pro dealer — genuine parts & warranty",
        "Somfy® motors — RTS & TaHoma® smart-home compatible",
        "Welded side-track hardware — no flapping in Texas wind",
        "Sunbrella® (solid or stripe) & Phifertex® Plus mesh fabrics",
        `Serving ${city.name} and surrounding ${city.region}`,
      ]}
      body={[
        {
          heading: `Why ${city.name} Homeowners & Businesses Pick Solair® Pro`,
          text: `Off-the-shelf patio screens aren't built for ${city.region} sun and wind. Our Solair® Pro drop screens are engineered for full-sun Texas patios: side-track guides keep the screen locked in a gust, mesh fabric blocks 90%+ of solar heat gain, and every unit is built to the exact opening of your ${city.name} space.`,
        },
        {
          heading: `Smart-Home Ready in ${city.name}`,
          text: `Every unit runs on a Somfy® tubular motor, so your ${city.name} drop screens join your existing Somfy TaHoma® hub alongside lighting, awnings, gates and ceiling fans — one remote, one app, sun and wind sensors that auto-adjust for ${city.region} weather.`,
          cta: { label: "See Smart Automation Details", href: "/exterior-vertical-shades#smart-automation" },
        },
        {
          heading: `Where We Install Drop Screens in ${city.name}`,
          text: `Back patios, covered outdoor kitchens, front porches, restaurant/bar patios, garage bays, second-story balconies, and commercial storefront openings. If it has an opening under ~20 ft wide, we can build a screen for it — wider spans get ganged into multi-unit systems.`,
        },
      ]}
      applications={[
        "Back Patio Drop Screens",
        "Outdoor Kitchen Enclosures",
        "Restaurant & Bar Patios",
        "Front Porches & Entries",
        "Second-Story Balconies",
        "Garage Bay Screens",
        "Commercial Storefronts",
        "Pool Cabanas",
      ]}
      faqs={[
        {
          q: `Do you install motorized drop screens in ${city.name}, TX?`,
          a: `Yes — as an authorized Solair® Pro dealer we install motorized exterior drop screens across ${city.name} and ${city.region}. On-site measurements are free.`,
        },
        {
          q: `How much do motorized drop screens cost in ${city.name}?`,
          a: `Every opening is different, but most single-opening motorized Solair® Pro drop screens in ${city.name} land in the mid four-figure range installed. Multi-bay outdoor kitchens and gang systems scale from there. Every quote is written and itemized.`,
        },
        {
          q: "Will the screen hold up to Texas wind?",
          a: `Yes — Solair® Pro uses a welded side-track (wind-lock) system, not a hanging drape. The screen fabric rides in a track on each side, so it doesn't flap or blow out. That's the whole point of choosing Solair® Pro over a builder-grade roll-up.`,
        },
        {
          q: `Can I control the screen from my phone in ${city.name}?`,
          a: `Yes. Every unit ships with a Somfy® motor, so you can pair it to a Somfy TaHoma® hub and control it from the TaHoma app, Alexa, Google Home or Apple Home — right alongside your other smart-home devices.`,
        },
        {
          q: "Sunbrella or Phifertex — which fabric should I pick?",
          a: `Sunbrella® acrylic (solid or stripe) is a full-block privacy screen with a soft, canvas look. Phifertex® Plus vinyl mesh is a see-through mesh that keeps your view while blocking heat and glare. On the quote form you can request samples of both before you decide.`,
        },
      ]}
      cityCallouts={[
        {
          to: "/exterior-vertical-shades" as string,
          city: "Solair® Pro Overview",
          anchorText: "Solair® Pro Exterior Vertical Shades",
          blurb: `See the full Solair® Pro product line, fabric options, and Somfy® smart-home integration.`,
        },
        ...nearby.slice(0, 3).map((n) => ({
          to: `/motorized-drop-screens/in/${n.slug}` as string,
          city: n.name,
          anchorText: `Motorized Drop Screens in ${n.name}, TX`,
          blurb: `We also install Solair® Pro motorized drop screens in nearby ${n.name} (${n.region}).`,
        })),
      ]}
      postCta={(
        <section className="border-t-4 border-navy-deep bg-white py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="text-center">
              <div className="font-display text-sm tracking-[0.3em] text-red">
                ★ MORE TEXAS CITIES WE INSTALL MOTORIZED DROP SCREENS IN
              </div>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                Solair® Pro Drop Screens Across Texas
              </h2>
            </div>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {DROP_SCREEN_CITIES.filter((c) => c.slug !== city.slug).map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/motorized-drop-screens/in/$city"
                    params={{ city: c.slug }}
                    className="block border-l-4 border-red bg-cream px-4 py-3 font-display text-sm font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    Drop Screens in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    />
  );
}
